import Login from './components/auth/Login'
import Layout from './components/layout/Layout'
import NotFound from './components/layout/errors/NotFound'
import Unauthorized from './components/layout/errors/Unauthorized'
import HelpPages from './components/help/index'
import RequireAuth from './components/auth/RequireAuth'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import useRefreshToken from './hooks/useRefreshToken'
import useAuth from './hooks/useAuth'
import routesConfig, { ROLES } from './routeConfig'
import ForgotPassword from './components/auth/ForgotPassword'
import AppInitiatedDialog from './components/common/dialogs/AppInitiatedDialog'
import UpdatePassword from './components/auth/UpdatePassword'
import { useAppDispatch } from './store/hooks'
import { getLocalStorageValue } from './components/common/utility/helpers'
import { changeTheme } from './features/theme/theme.slice'
import {
    changeTopNav,
    setDashboardChart,
    toggleSideNav,
} from './features/settings/settings.slice'

const App: React.FC = () => {
    const dispatch = useAppDispatch()

    const settings = getLocalStorageValue('settings', {})

    useEffect(() => {
        dispatch(setDashboardChart(settings.dashboard || {}))
        dispatch(toggleSideNav(settings.sideNavTpggled))
        dispatch(changeTopNav(settings.topNavFixed))
        dispatch(changeTheme(settings.theme || 'light'))
    }, [
        dispatch,
        settings.sideNavTpggled,
        settings.topNavFixed,
        settings.theme,
    ])

    const [initiatedTested, setInitiatedTested] = useState<boolean>(false)
    const [refreshed, setRefreshed] = useState(false)
    const { auth }: any = useAuth()
    const { refresh, initiated } = useRefreshToken()

    const effectRan = useRef(false)

    useEffect(() => {
        if (!effectRan.current) {
            if (!refreshed) {
                refresh(true)
                setRefreshed(true)
                setInitiatedTested(initiated)
            }
        }

        return () => {
            effectRan.current = true
        }
    }, [refresh, refreshed, initiated])

    return (
        <>
            {!initiatedTested && !initiated ? (
                <AppInitiatedDialog setInitiatedTested={setInitiatedTested} />
            ) : (
                <Routes>
                    <Route
                        path="login"
                        element={auth?.user ? <Navigate to={'/'} /> : <Login />}
                    />
                    <Route
                        path="forgot-password"
                        element={<ForgotPassword />}
                    />
                    <Route
                        path="update-password/:token"
                        element={<UpdatePassword />}
                    />
                    <Route path="unauthorized" element={<Unauthorized />} />

                    <Route
                        element={
                            <RequireAuth
                                allowedRoles={[
                                    ROLES.Admin,
                                    ROLES.Editor,
                                    ROLES.User,
                                    ROLES.Owner,
                                ]}
                            />
                        }
                    >
                        <Route
                            path="help/*"
                            element={<HelpPages.HelpHome />}
                        />
                        <Route path="/" element={<Layout />}>
                            {routesConfig.map((route, i) => {
                                if (!route?.static === true) {
                                    return route.protected ? (
                                        <Route
                                            key={i}
                                            element={
                                                <RequireAuth
                                                    allowedRoles={[
                                                        ...route.roles,
                                                        ROLES.Owner,
                                                    ]}
                                                />
                                            }
                                        >
                                            <Route
                                                path={route.path}
                                                element={<route.component />}
                                            />
                                        </Route>
                                    ) : (
                                        <Route
                                            key={i}
                                            element={<route.component />}
                                        />
                                    )
                                }

                                return ''
                            })}
                        </Route>
                    </Route>

                    <Route path="*" element={<NotFound />} />
                </Routes>
            )}
        </>
    )
}

export default App
